import { useState, useEffect, useRef } from 'react'

interface ProgressiveImageProps {
  src: string
  alt: string
  className?: string
  placeholderSrc?: string
  imgClassName?: string
  rootMargin?: string
}

export const ProgressiveImage = ({
  src,
  alt,
  className,
  placeholderSrc = '',
  imgClassName = '',
  rootMargin = '50px',
}: ProgressiveImageProps) => {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && imgRef.current) {
          const srcSet = `
            ${src} 1x,
            ${src.replace(/\.(jpg|png|webp)$/, '@2x.$1')} 2x,
            ${src.replace(/\.(jpg|png|webp)$/, '@3x.$1')} 3x
          `
          imgRef.current.src = src
          imgRef.current.srcset = srcSet
          observer.unobserve(entry.target)
        }
      },
      { rootMargin },
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current)
      }
    }
  }, [src, rootMargin])

  return (
    <picture className={`progressive-image ${loaded ? 'loaded' : ''} ${className ?? ''}`}>
      <source
        type="image/webp"
        className={imgClassName}
        srcSet={`
          ${src.replace(/\.(jpg|png)$/, '.webp')} 1x,
          ${src.replace(/\.(jpg|png|webp)$/, '@2x.webp')} 2x,
          ${src.replace(/\.(jpg|png|webp)$/, '@3x.webp')} 3x
        `}
      />
      <img
        ref={imgRef}
        src={placeholderSrc}
        alt={alt}
        onLoad={() => {
          setLoaded(true)
        }}
        className={imgClassName}
      />
    </picture>
  )
}
